import { useState, useEffect, useCallback } from 'react';
import { UserPreferences } from '../types';
import i18n from '../i18n';

const PREFERENCES_KEY = 'threatcompass_preferences';

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function writeCookie(name: string, value: string, days = 365): void {
  if (typeof document === 'undefined') return;
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
}

function detectInitialLanguage(): 'en'|'et'|'ru'|'ua' {
  try {
    // 1) Cookie wins
    const fromCookie = readCookie('tc_lang');
    if (fromCookie && ['en','et','ru','ua'].includes(fromCookie)) return fromCookie as any;
    // 2) URL locale
    const parts = (typeof window !== 'undefined' ? window.location.pathname : '').split('/').filter(Boolean);
    const loc = (parts[0] || '').toLowerCase();
    if (['en','et','ru','ua'].includes(loc)) return loc as any;
    // 3) i18n or navigator
    const guess = (i18n.language || (typeof navigator !== 'undefined' ? navigator.language : 'en')).toLowerCase();
    if (guess.startsWith('et')) return 'et';
    if (guess.startsWith('ru')) return 'ru';
    if (guess.startsWith('uk') || guess.startsWith('ua')) return 'ua';
    return 'en';
  } catch {
    return 'en';
  }
}

const defaultPreferences: UserPreferences = {
  language: detectInitialLanguage(),
  theme: 'light',
  chartType: 'line'
};

export const usePreferences = () => {
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);

  useEffect(() => {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Force light theme regardless of stored preference
      const forcedPreferences = { ...parsed, theme: 'light' };
      // If cookie language exists, prefer it over localStorage
      const cookieLang = readCookie('tc_lang');
      if (cookieLang && ['en','et','ru','ua'].includes(cookieLang)) {
        forcedPreferences.language = cookieLang as any;
      }
      setPreferences(forcedPreferences);

      // Apply language preference
      if (forcedPreferences.language) {
        i18n.changeLanguage(forcedPreferences.language);
      }
      
      // Force light theme always
      document.documentElement.classList.remove('dark');
      document.documentElement.setAttribute('data-theme', 'light');
      document.documentElement.style.colorScheme = 'light';
    } else {
      // Force light theme for new users
      document.documentElement.classList.remove('dark');
      document.documentElement.setAttribute('data-theme', 'light');
      document.documentElement.style.colorScheme = 'light';
    }
  }, []);

  const updatePreference = useCallback(<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => {
    setPreferences(prev => {
      const updated = { ...prev, [key]: value };
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(updated));
      
      // Apply changes immediately
      if (key === 'language') {
        i18n.changeLanguage(value as string);
      } else if (key === 'theme') {
        // Force light theme always, ignore theme changes
        document.documentElement.classList.remove('dark');
        document.documentElement.setAttribute('data-theme', 'light');
        document.documentElement.style.colorScheme = 'light';
      }
      
      // Persist language cookie
      if (key === 'language' && typeof value === 'string') {
        writeCookie('tc_lang', value as string);
      }
      return updated;
    });
  }, []);

  return {
    preferences,
    updatePreference
  };
};
